import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { formatCurrency } from "@/lib/crypto-data";
import { useCryptoPrices } from "@/hooks/useCryptoPrices";
import { useTranslation } from "react-i18next";
import { 
  Wallet, 
  Shield, 
  Zap, 
  Globe, 
  TrendingUp,
  CreditCard,
  ArrowLeft,
  RefreshCw
} from "lucide-react";

const Index = () => {
  const { cryptoAssets, loading: pricesLoading } = useCryptoPrices();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-crypto-purple/20 rounded-full blur-[120px] animate-float" style={{ animationDelay: '-3s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-crypto-gold/5 rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-in">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">{t('home.badge')}</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
              {t('home.heroTitle1')}
              <br />
              <span className="text-gradient">{t('home.heroTitle2')}</span>
            </h1>

            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '200ms' }}>
              {t('home.heroDesc')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '300ms' }}>
              <Link to="/auth?mode=signup">
                <Button className="h-14 px-8 text-lg gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity group">
                  {t('home.startNow')}
                  <ArrowLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="h-14 px-8 text-lg border-border text-foreground hover:bg-secondary">
                  {t('auth.signIn')}
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 mt-12 animate-fade-in" style={{ animationDelay: '400ms' }}>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="w-5 h-5 text-primary" />
                <span>{t('home.available')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CreditCard className="w-5 h-5 text-primary" />
                <span>{t('home.cards')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Zap className="w-5 h-5 text-primary" />
                <span>{t('home.instantOps')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Prices Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{t('home.livePrices')}</h2>
            <p className="text-muted-foreground">{t('home.livePricesDesc')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {cryptoAssets.map((crypto, index) => (
              <div 
                key={crypto.symbol}
                className="glass rounded-xl p-4 text-center hover:scale-105 transition-transform animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold"
                  style={{ background: crypto.color, color: 'hsl(var(--background))' }}
                >
                  {crypto.icon}
                </div>
                <p className="font-bold text-foreground">{crypto.symbol}</p>
                <p className="text-sm text-muted-foreground mb-2">{crypto.nameAr}</p>
                <p className="font-semibold text-foreground">{formatCurrency(crypto.price)}</p>
                <p className={`text-sm ${crypto.change24h >= 0 ? 'text-crypto-green' : 'text-crypto-red'}`}>
                  {crypto.change24h >= 0 ? '+' : ''}{crypto.change24h.toFixed(2)}%
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{t('home.whyUs')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('home.whyUsDesc')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: t('home.security'),
                description: t('home.securityDesc'),
                color: "bg-crypto-green/20 text-crypto-green",
              },
              {
                icon: Zap,
                title: t('home.speed'),
                description: t('home.speedDesc'),
                color: "bg-crypto-gold/20 text-crypto-gold",
              },
              {
                icon: Globe,
                title: t('home.global'),
                description: t('home.globalDesc'),
                color: "bg-primary/20 text-primary",
              },
            ].map((feature, index) => (
              <div 
                key={index}
                className="glass rounded-2xl p-8 text-center hover:scale-[1.02] transition-transform animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className={`w-16 h-16 rounded-2xl ${feature.color} mx-auto mb-6 flex items-center justify-center`}>
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="relative overflow-hidden rounded-3xl gradient-primary p-12 text-center">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">{t('home.ctaTitle')}</h2>
              <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">{t('home.ctaDesc')}</p>
              <Link to="/auth?mode=signup">
                <Button className="h-14 px-10 text-lg bg-white text-primary font-semibold hover:bg-white/90 transition-colors">
                  {t('home.createFreeAccount')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-foreground">{t('brand')}</span>
            </div>
            <p className="text-muted-foreground text-sm">{t('home.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
